import { TbAlertTriangle, TbCircleCheck, TbEqual, TbHelpCircle, TbTrendingDown, TbTrendingUp } from "react-icons/tb";
import { McaExpectedPaymentResult } from "dtos/loan.dto";

export type Verdict =
  | "no_payload"
  | "expected_unknown"
  | "actual_unknown"
  | "paid_off"
  | "on_track"
  | "ahead"
  | "behind";

export type PaymentBasis = McaExpectedPaymentResult["actual"]["basis"];
export type WindowEndReason = McaExpectedPaymentResult["window"]["endReason"];
export type MonthRow = McaExpectedPaymentResult["expected"]["breakdown"][number];
export type RevenueSource = MonthRow["revenueSource"];
export type VerdictMeta = {
  alert: "info" | "success" | "warning" | "error";
  dot: "default" | "processing" | "success" | "warning" | "error";
  icon: React.ReactNode;
  title: string;
  rail: string;
};

export const ON_TRACK_TOLERANCE = 0.05;

export const VERDICT_META: Record<Verdict, VerdictMeta> = {
  no_payload: {
    alert: "warning",
    dot: "default",
    icon: <TbAlertTriangle size={17} />,
    title: "Reconciliation hasn't run for this advance",
    rail: "Not run",
  },
  expected_unknown: {
    alert: "warning",
    dot: "warning",
    icon: <TbAlertTriangle size={17} />,
    title: "No revenue on file, so the required amount can't be calculated",
    rail: "Unmeasured",
  },
  actual_unknown: {
    alert: "warning",
    dot: "warning",
    icon: <TbHelpCircle size={17} />,
    title: "We know what the percentage required, not what was collected",
    rail: "Half measured",
  },
  paid_off: {
    alert: "success",
    dot: "success",
    icon: <TbCircleCheck size={17} />,
    title: "Balance is cleared",
    rail: "Cleared",
  },
  on_track: {
    alert: "success",
    dot: "success",
    icon: <TbEqual size={17} />,
    title: "Collections match the specified percentage",
    rail: "On track",
  },
  ahead: {
    alert: "info",
    dot: "processing",
    icon: <TbTrendingUp size={17} />,
    title: "Collected more than the percentage required",
    rail: "Ahead",
  },
  behind: {
    alert: "error",
    dot: "error",
    icon: <TbTrendingDown size={17} />,
    title: "Collected less than the percentage required",
    rail: "Behind",
  },
};

export const BASIS_LABEL: Record<PaymentBasis, string> = {
  balance: "Derived from the reported balance",
  missedInstallments: "Derived from a missed-payment count",
  assumedFull: "Assumed, every installment treated as cleared",
};

export const BASIS_SHORT: Record<PaymentBasis, string> = {
  balance: "From balance",
  missedInstallments: "From missed count",
  assumedFull: "Assumed",
};

export const END_REASON_LABEL: Record<WindowEndReason, string> = {
  lastPaidDate: "last known payment",
  asOf: "today, no payment date on file",
  derivedPayoff: "projected payoff from the schedule",
};

export const SOURCE_META: Record<RevenueSource, { color: string; label: string; note: string }> = {
  actual: { color: "green", label: "Statement", note: "Summed from transactions in that month" },
  "actual-partial": {
    color: "gold",
    label: "Part month",
    note: "Incomplete month, scaled up to a full-month equivalent",
  },
  interpolated: {
    color: "blue",
    label: "Interpolated",
    note: "Straight line between the two nearest months with data",
  },
  estimated: {
    color: "default",
    label: "Estimated",
    note: "Filled from the average of known months, or zero when none exist",
  },
};

type FlagRule = {
  match: (flag: string) => boolean;
  severity: "info" | "warning" | "error";
  title: string;
  body: (flag: string) => string;
};

const FLAG_RULES: FlagRule[] = [
  {
    match: (f) => f === "NO_REVENUE_DATA",
    severity: "error",
    title: "No revenue transactions were supplied",
    body: () =>
      "Every month in the window was filled with zero. The required amount is a placeholder, not a measurement.",
  },
  {
    match: (f) => f === "PAYMENTS_ASSUMED_FULL",
    severity: "error",
    title: "Collections were assumed, not observed",
    body: () =>
      "No balance and no missed-payment count reached the calculation, so it treated every elapsed installment as cleared.",
  },
  {
    match: (f) => f === "LOW_ACTUAL_REVENUE_COVERAGE",
    severity: "warning",
    title: "Under half the required amount traces to real revenue",
    body: () => "The rest came from estimates or interpolation between months with data.",
  },
  {
    match: (f) => f === "PERCENTAGE_NORMALISED_FROM_WHOLE_NUMBER",
    severity: "info",
    title: "The stored percentage was read as a whole number",
    body: () => "A stored value above 1 is divided by 100. Check the field if the share below looks wrong.",
  },
  {
    match: (f) => f === "EXPECTED_CAPPED_AT_PAYBACK",
    severity: "info",
    title: "Accrual stopped at full payback",
    body: () => "The percentage reached total payback inside the window, so later months are excluded.",
  },
  {
    match: (f) => f === "ADVANCE_APPEARS_PAID_OFF",
    severity: "info",
    title: "Balance reads zero or below",
    body: () => "The window closes at the payoff date implied by the contractual schedule.",
  },
  {
    match: (f) => f === "WINDOW_END_CLAMPED_TO_ASOF",
    severity: "warning",
    title: "The payment date sat in the future",
    body: () => "The window was pulled back to today, so the span below is shorter than the record implies.",
  },
  {
    match: (f) => f === "HISTORY_NOT_VISIBLE_FROM_FUNDED_DATE",
    severity: "warning",
    title: "Revenue history starts after the funding date",
    body: () => "The earliest months were estimated rather than observed.",
  },
  {
    match: (f) => f === "REVENUE_COVERAGE_GAP",
    severity: "warning",
    title: "There are gaps between the months with revenue",
    body: () => "Missing interior months were interpolated from their neighbours.",
  },
  {
    match: (f) => f === "TRAILING_MONTHS_ESTIMATED",
    severity: "warning",
    title: "The most recent months carry no revenue data",
    body: () => "Recent behaviour is the part you'd act on, and it's the part being estimated.",
  },
  {
    match: (f) => f === "CURRENT_MONTH_PARTIAL",
    severity: "info",
    title: "The latest month is incomplete",
    body: () => "Its revenue was scaled to a full-month equivalent before weighting.",
  },
  {
    match: (f) => f === "BALANCE_NEGATIVE",
    severity: "warning",
    title: "The reported balance is negative",
    body: () => "It was floored at zero for the collections figure.",
  },
  {
    match: (f) => f === "BALANCE_EXCEEDS_PAYBACK",
    severity: "warning",
    title: "The reported balance is larger than total payback",
    body: () => "One of the two figures on the loan record is wrong.",
  },
  {
    match: (f) => f.startsWith("BALANCE_IMPLIES_MISSED_PAYMENTS:"),
    severity: "warning",
    title: "The balance implies missed installments",
    body: (f) => `About ${f.split(":")[1]} installments' worth of the schedule is unaccounted for.`,
  },
];

export const describeFlag = (flag: string) => {
  const hit = FLAG_RULES.find((rule) => rule.match(flag));
  if (!hit) return { severity: "info" as const, title: flag, body: "Unrecognised diagnostic flag." };
  return { severity: hit.severity, title: hit.title, body: hit.body(flag) };
};

export const pctLabel = (fraction: number, digits = 2) => `${(fraction * 100).toFixed(digits).replace(/\.?0+$/, "")}%`;

export const shortDate = (value: string | Date | null | undefined) => {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

export const monthLabel = (month: string) => {
  const d = new Date(`${month}-01T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return month;
  return d.toLocaleDateString(undefined, { month: "short", year: "2-digit", timeZone: "UTC" });
};
